import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Search,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  AlertTriangle,
} from "lucide-react";
import { useAdminBotChatConversationsQuery } from "@/generated/graphql";
import { formatDateTime } from "@/lib/format";

const AdminBotConversations = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [channel, setChannel] = useState("all");
  const [linked, setLinked] = useState("all");
  const [hasError, setHasError] = useState(false);
  const [page, setPage] = useState(1);

  const { data, loading } = useAdminBotChatConversationsQuery({
    variables: {
      page,
      pageSize: 25,
      search: search || undefined,
      channel: channel === "all" ? undefined : channel,
      linked: linked === "all" ? undefined : linked === "linked",
      hasError: hasError || undefined,
    },
    fetchPolicy: "cache-and-network",
  });

  const conversations = data?.adminBotChatConversations?.data || [];
  const pageCount = data?.adminBotChatConversations?.pageCount || 1;
  const total = data?.adminBotChatConversations?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">{t("admin.botConversations.title")}</h1>
              <p className="text-muted-foreground">{t("admin.botConversations.subtitle")}</p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t("admin.botConversations.title")}</CardTitle>
                <span className="text-sm text-muted-foreground">
                  {t("admin.common.total")}: {total}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="flex flex-wrap gap-2 mb-4 items-center">
                <Input
                  placeholder={t("admin.botConversations.searchPlaceholder")}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="max-w-sm"
                />
                <Select
                  value={channel}
                  onValueChange={(v) => {
                    setChannel(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("admin.botConversations.allChannels")}</SelectItem>
                    <SelectItem value="telegram">Telegram</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={linked}
                  onValueChange={(v) => {
                    setLinked(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("admin.botConversations.allLinks")}</SelectItem>
                    <SelectItem value="linked">{t("admin.botConversations.linkedOnly")}</SelectItem>
                    <SelectItem value="unlinked">{t("admin.botConversations.unlinkedOnly")}</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Switch
                    id="hasError"
                    checked={hasError}
                    onCheckedChange={(c) => {
                      setHasError(c);
                      setPage(1);
                    }}
                  />
                  <Label htmlFor="hasError" className="text-sm">
                    {t("admin.botConversations.hasErrors")}
                  </Label>
                </div>
                <Button type="submit" variant="outline">
                  <Search className="w-4 h-4" />
                </Button>
              </form>

              {loading && !data ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : (
                <>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("admin.botConversations.columnChannel")}</TableHead>
                          <TableHead>{t("admin.botConversations.columnIdentity")}</TableHead>
                          <TableHead>{t("admin.botConversations.columnUser")}</TableHead>
                          <TableHead>{t("admin.botConversations.columnMessages")}</TableHead>
                          <TableHead>{t("admin.botConversations.columnLastSeen")}</TableHead>
                          <TableHead>{t("admin.users.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {conversations.map((c) => (
                          <TableRow key={c.id}>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">{c.channel}</Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{c.channel_user_id}</TableCell>
                            <TableCell>
                              {c.user ? (
                                <Link
                                  to={`/admin/users/${c.user.id}`}
                                  className="text-primary hover:underline"
                                >
                                  {c.user.name || c.user.email}
                                </Link>
                              ) : (
                                <Badge variant="outline" className="text-orange-500 border-orange-500">
                                  {t("admin.botConversations.unlinked")}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="inline-flex items-center gap-2">
                                {c.message_count ?? 0}
                                {(c.error_count ?? 0) > 0 && (
                                  <span className="inline-flex items-center gap-1 text-red-500 text-xs">
                                    <AlertTriangle className="w-3.5 h-3.5" /> {c.error_count}
                                  </span>
                                )}
                              </span>
                            </TableCell>
                            <TableCell>
                              {c.last_seen_at ? formatDateTime(c.last_seen_at) : "-"}
                            </TableCell>
                            <TableCell>
                              <Link to={`/admin/bot-conversations/${c.id}`}>
                                <Button variant="ghost" size="sm">
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                        {conversations.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                              {t("admin.botConversations.noConversations")}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-muted-foreground">
                      {t("admin.common.page")} {page} {t("admin.common.of")} {pageCount}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                        disabled={page === pageCount}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </Layout>
  );
};

export default AdminBotConversations;
